import React, {useEffect, useMemo, useState} from 'react'
import {Box, CircularProgress, Switch, Tab, Tabs, Tooltip, Typography} from '@mui/material'
import RestoreIcon from '@mui/icons-material/Restore'
import CloseIcon from '@mui/icons-material/Close'
import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import SearchBar from 'src/components-new/inputs/SearchBar'
import Button from 'src/components-new/primitives/Button'
import StatusPill from 'src/components-new/primitives/StatusPill'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import AppDialog from 'src/components-new/feedback/AppDialog'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {useAppSelector} from 'src/hooks'
import PrimaryTherapyDetail from 'src/pages-new/patient/tabs/therapy/PrimaryTherapyDetail'
import SecondaryTherapyDetail from 'src/pages-new/patient/tabs/therapy/SecondaryTherapyDetail'
import {formatTherapyState} from 'src/pages-new/patient/tabs/therapy/therapyFormatters'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {
  canOnSecondaryTherapy,
  formatReminderStatus,
  isReminderActive,
  REMINDER_STATUSES,
  SECONDARY_THERAPY_LOCKED_TOOLTIP,
  SecondaryTherapyDataSchemaElement,
  resolveSecondaryTherapyDataSchema,
  secondaryTherapyErrorMessage
} from 'src/constants/secondaryTherapyFields'
import SecondaryTherapyDialogNew from './dialog/SecondaryTherapyDialogNew'

// New-style equivalent of src/pages/therapies/Therapies.tsx — standalone /therapies page with full CRUD
// for accessory ("secondary") therapies. Primary therapies are read-only here (same as the legacy page).

type SubTab = 'primaryTherapies' | 'secondaryTherapies'

function PrimaryTherapiesPanel(): JSX.Element {
  const [therapies, setTherapies] = useState<Record<string, any>[]>([])
  const [status, setStatus] = useState<string>()
  const [search, setSearch] = useState<string>('')
  const toast = useToast()

  const fetchTherapies = (): void => {
    apiClient.getTherapies(setStatus)
      .then((data: Record<string, any>[]) => setTherapies(Array.isArray(data) ? data : []))
      .catch(() => setStatus(STATUS.REJECTED))
  }

  useEffect(() => {
    fetchTherapies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (status === STATUS.REJECTED) {
      toast.error('Impossibile caricare le terapie.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  const filteredTherapies = therapies.filter((therapy) =>
    (therapy.therapyType?.name ?? '').toLowerCase().includes(search.toLowerCase()))

  return (
    <Box>
      <Box sx={{mb: 2, maxWidth: 320}}>
        <SearchBar value={search} onChange={setSearch} placeholder="Cerca terapia..." fullWidth />
      </Box>

      {status === STATUS.PENDING && (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress />
        </Box>
      )}

      {status !== STATUS.PENDING && filteredTherapies.length === 0 && (
        <EmptyState title="Nessuna terapia trovata" />
      )}

      {status !== STATUS.PENDING && filteredTherapies.length > 0 && (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          {filteredTherapies.map((therapy) => {
            const isInactive = therapy.state === 'NOT_ACTIVE'
            return (
              <Card
                key={therapy._id}
                title={(
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    {therapy.therapyType?.name ?? ''}
                    {therapy.state && (
                      <StatusPill
                        label={formatTherapyState(therapy.state)}
                        color={isInactive ? 'error' : 'success'}
                      />
                    )}
                  </Box>
                )}
              >
                <PrimaryTherapyDetail therapy={therapy} />
              </Card>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

// On/off indicator of whether the assumption reminder is going out for this therapy, pinned to
// the card's far right via Card's `action` slot. Shared between primary and secondary therapies —
// `reminderStatus` is the same field on both (see constants/secondaryTherapyFields.ts). Read-only
// (no `onClick`, rendered disabled) for primary therapies, which are read-only here altogether;
// secondary therapies pass `onClick` to flip `reminderStatus` between ACTIVE and DISABLED directly
// from the list, since the edit form only ever writes `reminderStatus` on create (see
// secondaryTherapyFormUtils).
function ReminderToggle({therapy, onClick}: {therapy: Record<string, any>; onClick?: () => void}): JSX.Element {
  const isActive = isReminderActive(therapy)
  const label = formatReminderStatus(therapy)

  const Bell = isActive ? NotificationsIcon : NotificationsOffOutlinedIcon

  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
      <Bell fontSize="small" sx={{color: isActive ? 'primary.main' : NEUTRAL_SCALE[500]}} />
      <Tooltip title={label}>
        <span>
          <Switch
            checked={isActive}
            onChange={onClick}
            disabled={!onClick}
            inputProps={{'aria-label': label}}
          />
        </span>
      </Tooltip>
    </Box>
  )
}

function SecondaryTherapiesPanel(): JSX.Element {
  const toast = useToast()
  // Same source the "Aggiungi terapia accessoria" wizard uses — keeps the card view and the
  // create/edit form showing the same set of fields.
  const dataSchema: SecondaryTherapyDataSchemaElement[] | undefined = useAppSelector(
    (state) => resolveSecondaryTherapyDataSchema(state.pspConfigReducer.data.therapyConfigurations?.secondaryTherapiesConfiguration)
  )
  const [secondaryTherapies, setSecondaryTherapies] = useState<Record<string, any>[]>([])
  const [status, setStatus] = useState<string>()
  const [search, setSearch] = useState<string>('')
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [editingTherapy, setEditingTherapy] = useState<Record<string, any> | undefined>(undefined)
  const [deletingTherapy, setDeletingTherapy] = useState<Record<string, any> | undefined>(undefined)
  const [statusDelete, setStatusDelete] = useState<string>()
  const [statusToggle, setStatusToggle] = useState<string>()
  const [statusReminderToggle, setStatusReminderToggle] = useState<string>()

  const fetchSecondaryTherapies = (): void => {
    apiClient.getSecondaryTherapies(setStatus)
      .then((data: Record<string, any>[]) => setSecondaryTherapies(Array.isArray(data) ? data : []))
      .catch(() => setStatus(STATUS.REJECTED))
  }

  useEffect(() => {
    fetchSecondaryTherapies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (status === STATUS.REJECTED) {
      toast.error('Impossibile caricare le altre terapie.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  useEffect(() => {
    if (statusDelete === STATUS.FULFILLED) {
      toast.success('Terapia accessoria eliminata con successo')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusDelete])

  useEffect(() => {
    if (statusToggle === STATUS.FULFILLED) {
      toast.success('Stato della terapia accessoria aggiornato')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusToggle])

  useEffect(() => {
    if (statusReminderToggle === STATUS.FULFILLED) {
      toast.success('Promemoria di assunzione aggiornato')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusReminderToggle])

  const openCreateDialog = (): void => {
    setEditingTherapy(undefined)
    setIsDialogOpen(true)
  }

  const openEditDialog = (secondaryTherapy: Record<string, any>): void => {
    setEditingTherapy(secondaryTherapy)
    setIsDialogOpen(true)
  }

  const onDialogSuccess = (): void => {
    setIsDialogOpen(false)
    fetchSecondaryTherapies()
  }

  // A failed write gets its toast here rather than in the status effects above: only the onError
  // callback knows the status code, and a 403 (therapy entered by the centre, therapy-service
  // specs/2026-10-secondary-therapy-authz.md) has its own message.
  const runWrite = async (
    write: (onError: (message?: string, statusCode?: number) => void) => Promise<unknown>,
    fallbackMessage: string
  ): Promise<void> => {
    let failed = false
    let errorStatusCode: number | undefined
    await write((_message, statusCode) => {
      failed = true
      errorStatusCode = statusCode
    })
    if (failed) {
      toast.error(
        secondaryTherapyErrorMessage(errorStatusCode, fallbackMessage),
        errorStatusCode === 403 ? {duration: 8000} : undefined
      )
    }
  }

  const onConfirmDelete = async (): Promise<void> => {
    if (!deletingTherapy) return
    const therapyId = deletingTherapy._id
    await runWrite(
      (onError) => apiClient.deleteSecondaryTherapy(therapyId, setStatusDelete, onError),
      'Errore nell\'eliminazione della terapia accessoria.'
    )
    setDeletingTherapy(undefined)
    fetchSecondaryTherapies()
  }

  const onToggleStateClick = async (secondaryTherapy: Record<string, any>): Promise<void> => {
    // Transitional fallback to the legacy `drug.state` for documents that predate the root
    // `state` field (RF18 of therapy-service specs/2026-09-notification-secondaryTherapy.md) and
    // haven't been backfilled yet. Drop the fallback once the migration script has run everywhere.
    const currentState = secondaryTherapy.state ?? secondaryTherapy.drug?.state
    const newState = currentState === 'NOT_ACTIVE' ? 'ACTIVE' : 'NOT_ACTIVE'
    await runWrite(
      (onError) => apiClient.patchSecondaryTherapy(
        secondaryTherapy._id,
        {
          state: newState,
          // Deactivating the therapy also switches its assumption reminder off (RF18 of
          // therapy-service specs/2026-09-notification-secondaryTherapy.md) — reactivating it does
          // NOT switch the reminder back on, that stays a separate, explicit choice.
          ...(newState === 'NOT_ACTIVE' ? {reminderStatus: REMINDER_STATUSES.DISABLED} : {})
        },
        setStatusToggle,
        onError
      ),
      'Errore nell\'aggiornamento dello stato della terapia accessoria.'
    )
    fetchSecondaryTherapies()
  }

  const onToggleReminderClick = async (secondaryTherapy: Record<string, any>): Promise<void> => {
    const newReminderStatus = isReminderActive(secondaryTherapy) ? REMINDER_STATUSES.DISABLED : REMINDER_STATUSES.ACTIVE
    await runWrite(
      (onError) => apiClient.patchSecondaryTherapy(
        secondaryTherapy._id,
        {reminderStatus: newReminderStatus},
        setStatusReminderToggle,
        onError
      ),
      'Errore nell\'aggiornamento del promemoria di assunzione.'
    )
    fetchSecondaryTherapies()
  }

  const filteredSecondaryTherapies = secondaryTherapies.filter((secondaryTherapy) =>
    (secondaryTherapy.drug?.name ?? '').toLowerCase().includes(search.toLowerCase()))

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1}}>
        <Box sx={{maxWidth: 320, flex: 1}}>
          <SearchBar value={search} onChange={setSearch} placeholder="Cerca terapia..." fullWidth />
        </Box>
        <Button variant="primary" onClick={openCreateDialog}>
          Aggiungi
        </Button>
      </Box>

      {status === STATUS.PENDING && (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress />
        </Box>
      )}

      {status !== STATUS.PENDING && filteredSecondaryTherapies.length === 0 && (
        <EmptyState title="Nessuna terapia accessoria trovata" />
      )}

      {status !== STATUS.PENDING && filteredSecondaryTherapies.length > 0 && (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          {filteredSecondaryTherapies.map((secondaryTherapy) => {
            // Transitional fallback, see onToggleStateClick above.
            const state = secondaryTherapy.state ?? secondaryTherapy.drug?.state
            const isInactive = state === 'NOT_ACTIVE'
            const canUpdate = canOnSecondaryTherapy(secondaryTherapy, 'update')
            const canDelete = canOnSecondaryTherapy(secondaryTherapy, 'delete')
            const canUpdateReminder = canOnSecondaryTherapy(secondaryTherapy, 'updateReminder')
            return (
              <Card
                key={secondaryTherapy._id}
                title={(
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    {secondaryTherapy.drug?.name ?? 'Terapia accessoria'}
                    {state && (
                      <StatusPill
                        label={formatTherapyState(state)}
                        color={isInactive ? 'error' : 'success'}
                      />
                    )}
                  </Box>
                )}
                action={(
                  <ReminderToggle
                    therapy={secondaryTherapy}
                    onClick={canUpdateReminder ? () => onToggleReminderClick(secondaryTherapy) : undefined}
                  />
                )}
              >
                <SecondaryTherapyDetail secondaryTherapy={secondaryTherapy} dataSchema={dataSchema} />
                <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 1}}>
                  <ActionIconButton
                    variant="edit"
                    tooltip={canUpdate ? 'Modifica' : SECONDARY_THERAPY_LOCKED_TOOLTIP}
                    disabled={isInactive || !canUpdate}
                    onClick={() => openEditDialog(secondaryTherapy)}
                  />
                  <ActionIconButton
                    variant="delete"
                    tooltip={canDelete ? 'Elimina' : SECONDARY_THERAPY_LOCKED_TOOLTIP}
                    disabled={isInactive || !canDelete}
                    onClick={() => setDeletingTherapy(secondaryTherapy)}
                  />
                  {/* Changes `state`: the same permission as the edit form */}
                  <Tooltip title={canUpdate ? '' : SECONDARY_THERAPY_LOCKED_TOOLTIP}>
                    <span style={{display: 'inline-flex'}}>
                      <Button
                        variant="ghost"
                        startIcon={isInactive ? <RestoreIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                        disabled={!canUpdate}
                        onClick={() => onToggleStateClick(secondaryTherapy)}
                      >
                        {isInactive ? 'Riattiva' : 'Disattiva'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Card>
            )
          })}
        </Box>
      )}

      <SecondaryTherapyDialogNew
        open={isDialogOpen}
        secondaryTherapy={editingTherapy}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={onDialogSuccess}
      />

      <AppDialog
        open={Boolean(deletingTherapy)}
        onClose={() => setDeletingTherapy(undefined)}
        title="Elimina terapia accessoria"
        actions={(
          <>
            <Button variant="secondary" onClick={() => setDeletingTherapy(undefined)}>
              Annulla
            </Button>
            <Button variant="danger" loading={statusDelete === STATUS.PENDING} onClick={onConfirmDelete}>
              Elimina
            </Button>
          </>
        )}
      >
        <Typography variant="body2">
          Sei sicuro di voler eliminare la terapia accessoria
          {' '}
          <strong>{deletingTherapy?.drug?.name ?? ''}</strong>
          ? L&apos;operazione non è reversibile.
        </Typography>
      </AppDialog>
    </Box>
  )
}

export default function TherapiesNew(): JSX.Element {
  const sidebarItems = useAppSelector((state) => state.pspConfigReducer.data.sidebarItems)
  const showPrimaryTherapies = Boolean(sidebarItems?.primaryTherapies?.show)
  const showSecondaryTherapies = Boolean(sidebarItems?.secondaryTherapies?.show)

  const availableTabs = useMemo(() => ([
    ...(showPrimaryTherapies ? [{key: 'primaryTherapies' as SubTab, label: sidebarItems?.primaryTherapies?.label ?? 'Terapie'}] : []),
    ...(showSecondaryTherapies ? [{key: 'secondaryTherapies' as SubTab, label: sidebarItems?.secondaryTherapies?.label ?? 'Altre terapie'}] : []),
  ]), [showPrimaryTherapies, showSecondaryTherapies, sidebarItems])

  const [activeTab, setActiveTab] = useState<SubTab | undefined>(availableTabs[0]?.key)

  useEffect(() => {
    if (!availableTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(availableTabs[0]?.key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTabs])

  if (availableTabs.length === 0) {
    return <EmptyState title="Nessuna terapia disponibile" description="Non ci sono terapie configurate per questo profilo." />
  }

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_e, val: SubTab) => setActiveTab(val)}
        sx={{
          mb: 3,
          '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
          '& .MuiTabs-indicator': {height: 2},
        }}
      >
        {availableTabs.map((tab) => <Tab key={tab.key} value={tab.key} label={tab.label} />)}
      </Tabs>

      {activeTab === 'primaryTherapies' && <PrimaryTherapiesPanel />}
      {activeTab === 'secondaryTherapies' && <SecondaryTherapiesPanel />}
    </Box>
  )
}
